import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, LineChart, Line, Cell } from 'recharts';
import { ArrowUpIcon, ArrowDownIcon, TrendingUpIcon, AlertTriangleIcon, ArrowRightIcon, FilterIcon, BellIcon } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import ImpactCard from '../components/ImpactCard';
import PolicyChangeTimeline from '../components/PolicyChangeTimeline';
const Dashboard = () => {
  const [timeframe, setTimeframe] = useState('30days');
  const [viewMode, setViewMode] = useState('impact');
  // Sample data for impact metrics
  const impactMetrics = [{
    title: 'Coverage Loss',
    value: '23.7M',
    change: '+4.2M',
    trend: 'up',
    status: 'negative',
    description: 'Insured lives with negative coverage changes in the last 30 days'
  }, {
    title: 'Coverage Gain',
    value: '5.2M',
    change: '+1.8M',
    trend: 'up',
    status: 'positive',
    description: 'Insured lives with positive coverage changes in the last 30 days'
  }, {
    title: 'Critical Reviews',
    value: '7',
    change: '+3',
    trend: 'up',
    status: 'warning',
    description: 'Upcoming policy reviews with high-impact potential in next 90 days'
  }, {
    title: 'Opportunity States',
    value: '12',
    change: '+5',
    trend: 'up',
    status: 'opportunity',
    description: 'States with improving coverage trends for your products'
  }];
  // Sample data for policy change trend
  const policyChangeTrend = [{
    month: 'Jan',
    positive: 8,
    negative: 14,
    noPolicy: 3
  }, {
    month: 'Feb',
    positive: 10,
    negative: 12,
    noPolicy: 4
  }, {
    month: 'Mar',
    positive: 12,
    negative: 15,
    noPolicy: 2
  }, {
    month: 'Apr',
    positive: 9,
    negative: 18,
    noPolicy: 3
  }, {
    month: 'May',
    positive: 11,
    negative: 20,
    noPolicy: 5
  }, {
    month: 'Jun',
    positive: 15,
    negative: 17,
    noPolicy: 4
  }];
  // Sample data for impact by region
  const impactByRegion = [{
    region: 'Northeast',
    impact: 78,
    color: '#ef4444'
  }, {
    region: 'Southeast',
    impact: 65,
    color: '#ef4444'
  }, {
    region: 'Midwest',
    impact: 43,
    color: '#f97316'
  }, {
    region: 'Southwest',
    impact: 32,
    color: '#eab308'
  }, {
    region: 'West',
    impact: 28,
    color: '#22c55e'
  }];
  // Sample data for high-impact policy changes
  const highImpactChanges = [{
    id: 1,
    title: 'Peripheral Nerve Stimulation Coverage Policy',
    payer: 'UnitedHealthcare',
    changeType: 'negative',
    changeDate: 'June 15, 2025',
    impactedLives: '18.2M',
    changeDescription: 'Reclassified as experimental/investigational with denial of coverage',
    affectedProducts: ['Freedom PNS System', 'Sprint PNS System'],
    recommendations: ['Submit clinical evidence package by July 30', 'Request policy reconsideration meeting']
  }, {
    id: 2,
    title: 'Implantable Neurostimulation Devices',
    payer: 'Aetna',
    changeType: 'negative',
    changeDate: 'June 3, 2025',
    impactedLives: '5.4M',
    changeDescription: 'Added prior authorization requirement with stricter clinical criteria',
    affectedProducts: ['Freedom PNS System'],
    recommendations: ['Develop PA support program for physicians', 'Create clinical documentation templates']
  }, {
    id: 3,
    title: 'Peripheral Nerve Field Stimulation',
    payer: 'Blue Cross Blue Shield of North Carolina',
    changeType: 'positive',
    changeDate: 'May 28, 2025',
    impactedLives: '3.8M',
    changeDescription: 'Approved for specific chronic pain conditions with documented criteria',
    affectedProducts: ['Freedom PNS System', 'Sprint PNS System'],
    recommendations: ['Create physician education materials on new coverage', 'Update sales training for NC territory']
  }];
  // Sample data for policy change timeline
  const policyChangeEvents = [{
    id: 1,
    date: 'June 15, 2025',
    payer: 'UnitedHealthcare',
    title: 'Peripheral Nerve Stimulation Coverage Policy',
    type: 'negative',
    impact: 'high'
  }, {
    id: 2,
    date: 'June 3, 2025',
    payer: 'Aetna',
    title: 'Implantable Neurostimulation Devices',
    type: 'negative',
    impact: 'medium'
  }, {
    id: 3,
    date: 'May 28, 2025',
    payer: 'Blue Cross Blue Shield of North Carolina',
    title: 'Peripheral Nerve Field Stimulation',
    type: 'positive',
    impact: 'medium'
  }, {
    id: 4,
    date: 'May 15, 2025',
    payer: 'Cigna',
    title: 'Pain Management Devices and Procedures',
    type: 'negative',
    impact: 'low'
  }, {
    id: 5,
    date: 'May 7, 2025',
    payer: 'Humana',
    title: 'Neurostimulation for Chronic Pain',
    type: 'positive',
    impact: 'low'
  }, {
    id: 6,
    date: 'April 22, 2025',
    payer: 'Anthem',
    title: 'Peripheral Nerve Stimulation for Pain Management',
    type: 'negative',
    impact: 'high'
  }];
  return <div className="px-4 py-6 md:px-6 lg:px-8 bg-slate-50" data-id="element-474">
      {/* Dashboard Header with Insights Summary */}
      <div className="mb-6" data-id="element-475">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-476">
          <div data-id="element-477">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-478">
              Policy Intelligence Dashboard
            </h1>
            <p className="mt-1 text-sm text-gray-500" data-id="element-479">
              Synthesized insights from policy changes across all payers
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center space-x-4" data-id="element-480">
            <div className="flex p-1 bg-white rounded-lg shadow" data-id="element-481">
              <button className={`px-3 py-1.5 text-sm font-medium rounded ${timeframe === '30days' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setTimeframe('30days')} data-id="element-482">
                30 Days
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded ${timeframe === '90days' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setTimeframe('90days')} data-id="element-483">
                90 Days
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded ${timeframe === '12months' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setTimeframe('12months')} data-id="element-484">
                12 Months
              </button>
            </div>
            <button className="p-2 text-gray-500 bg-white rounded-full shadow hover:text-gray-700" data-id="element-485">
              <FilterIcon className="w-5 h-5" data-id="element-486" />
            </button>
            <div className="relative" data-id="element-487">
              <button className="p-2 text-gray-500 bg-white rounded-full shadow hover:text-gray-700" data-id="element-488">
                <BellIcon className="w-5 h-5" data-id="element-489" />
              </button>
              <span className="absolute top-0 right-0 flex items-center justify-center w-4 h-4 text-xs text-white bg-red-500 rounded-full" data-id="element-490">
                3
              </span>
            </div>
          </div>
        </div>
        {/* Alert Banner for Critical Updates */}
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-491">
          <div className="flex items-start" data-id="element-492">
            <div className="flex-shrink-0" data-id="element-493">
              <AlertTriangleIcon className="w-5 h-5 text-amber-500" data-id="element-494" />
            </div>
            <div className="ml-3" data-id="element-495">
              <h3 className="text-sm font-medium text-amber-800" data-id="element-496">
                Critical Policy Alert
              </h3>
              <div className="mt-1 text-sm text-amber-700" data-id="element-497">
                <p data-id="element-498">
                  UnitedHealthcare policy change impacts 18.2M lives across 28
                  states. Effective July 1, 2025.
                </p>
              </div>
              <div className="mt-2" data-id="element-499">
                <button className="text-sm font-medium text-amber-800 hover:text-amber-900" data-id="element-500">
                  View Impact Analysis →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Impact Metrics */}
      <div className="grid grid-cols-1 gap-6 mb-8 sm:grid-cols-2 lg:grid-cols-4" data-id="element-501">
        {impactMetrics.map((metric, index) => <div key={index} className="relative overflow-hidden bg-white rounded-lg shadow transition-all hover:shadow-md" data-id="element-502">
            <div className={`absolute top-0 left-0 w-1 h-full ${metric.status === 'negative' ? 'bg-red-500' : metric.status === 'positive' ? 'bg-green-500' : metric.status === 'warning' ? 'bg-amber-500' : 'bg-blue-500'}`} data-id="element-503"></div>
            <div className="p-5" data-id="element-504">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-505">
                {metric.title}
              </h3>
              <div className="flex items-baseline mt-2" data-id="element-506">
                <p className="text-3xl font-bold text-gray-900" data-id="element-507">
                  {metric.value}
                </p>
                <p className={`ml-2 text-sm font-medium ${metric.trend === 'up' && metric.status === 'negative' ? 'text-red-600' : metric.trend === 'up' && metric.status === 'positive' ? 'text-green-600' : metric.trend === 'down' && metric.status === 'negative' ? 'text-green-600' : metric.trend === 'down' && metric.status === 'positive' ? 'text-red-600' : 'text-gray-500'}`} data-id="element-508">
                  {metric.change}
                  {metric.trend === 'up' ? <ArrowUpIcon className="inline w-3 h-3 ml-1" data-id="element-509" /> : <ArrowDownIcon className="inline w-3 h-3 ml-1" data-id="element-510" />}
                </p>
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-511">{metric.description}</p>
            </div>
          </div>)}
      </div>

      {/* View Selector */}
      <div className="flex mb-6 border-b border-gray-200" data-id="element-512">
        <button className={`px-4 py-2 text-sm font-medium border-b-2 ${viewMode === 'impact' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setViewMode('impact')} data-id="element-513">
          Impact Analysis
        </button>
        <button className={`px-4 py-2 text-sm font-medium border-b-2 ${viewMode === 'timeline' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setViewMode('timeline')} data-id="element-514">
          Policy Timeline
        </button>
        <button className={`px-4 py-2 text-sm font-medium border-b-2 ${viewMode === 'trends' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setViewMode('trends')} data-id="element-515">
          Coverage Trends
        </button>
      </div>

      {viewMode === 'impact' && <>
          {/* High Impact Policy Changes */}
          <div className="mb-8" data-id="element-516">
            <div className="flex items-center justify-between mb-4" data-id="element-517">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-518">
                High-Impact Policy Changes
              </h2>
              <button className="flex items-center text-sm text-blue-600 hover:text-blue-800" data-id="element-519">
                View All <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-520" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-id="element-521">
              {highImpactChanges.map(change => <ImpactCard key={change.id} change={change} data-id="element-522" />)}
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 gap-6 mb-8 lg:grid-cols-2" data-id="element-523">
            {/* Policy Change Trend Chart */}
            <div className="p-5 bg-white rounded-lg shadow" data-id="element-524">
              <div className="flex items-center justify-between mb-4" data-id="element-525">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-526">
                  Policy Change Trend
                </h3>
                <select className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-527">
                  <option data-id="element-528">Last 6 Months</option>
                  <option data-id="element-529">Last 12 Months</option>
                  <option data-id="element-530">Last 24 Months</option>
                </select>
              </div>
              <div className="h-64" data-id="element-531">
                <ResponsiveContainer width="100%" height="100%" data-id="element-532">
                  <AreaChart data={policyChangeTrend} margin={{
                top: 10,
                right: 30,
                left: 0,
                bottom: 0
              }} data-id="element-533">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" data-id="element-534" />
                    <XAxis dataKey="month" data-id="element-535" />
                    <YAxis data-id="element-536" />
                    <Tooltip data-id="element-537" />
                    <Area type="monotone" dataKey="negative" stackId="1" stroke="#ef4444" fill="#fecaca" data-id="element-538" />
                    <Area type="monotone" dataKey="positive" stackId="1" stroke="#22c55e" fill="#bbf7d0" data-id="element-539" />
                    <Area type="monotone" dataKey="noPolicy" stackId="1" stroke="#eab308" fill="#fef08a" data-id="element-540" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center mt-4 space-x-6" data-id="element-541">
                <div className="flex items-center" data-id="element-542">
                  <div className="w-3 h-3 bg-red-400 rounded-full" data-id="element-543"></div>
                  <span className="ml-2 text-xs text-gray-600" data-id="element-544">Negative</span>
                </div>
                <div className="flex items-center" data-id="element-545">
                  <div className="w-3 h-3 bg-green-400 rounded-full" data-id="element-546"></div>
                  <span className="ml-2 text-xs text-gray-600" data-id="element-547">Positive</span>
                </div>
                <div className="flex items-center" data-id="element-548">
                  <div className="w-3 h-3 bg-yellow-400 rounded-full" data-id="element-549"></div>
                  <span className="ml-2 text-xs text-gray-600" data-id="element-550">No Policy</span>
                </div>
              </div>
            </div>

            {/* Impact by Region Chart */}
            <div className="p-5 bg-white rounded-lg shadow" data-id="element-551">
              <div className="flex items-center justify-between mb-4" data-id="element-552">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-553">
                  Impact by Region
                </h3>
                <select className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-554">
                  <option data-id="element-555">By Lives Impacted</option>
                  <option data-id="element-556">By Policy Count</option>
                  <option data-id="element-557">By Revenue Impact</option>
                </select>
              </div>
              <div className="h-64" data-id="element-558">
                <ResponsiveContainer width="100%" height="100%" data-id="element-559">
                  <BarChart data={impactByRegion} layout="vertical" margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-560">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" data-id="element-561" />
                    <XAxis type="number" data-id="element-562" />
                    <YAxis dataKey="region" type="category" width={80} data-id="element-563" />
                    <Tooltip data-id="element-564" />
                    <Bar dataKey="impact" radius={[0, 4, 4, 0]} data-id="element-565">
                      {impactByRegion.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-566" />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 text-center" data-id="element-567">
                <span className="text-xs text-gray-500" data-id="element-568">
                  Impact Score (higher = more negative impact)
                </span>
              </div>
            </div>
          </div>
        </>}

      {viewMode === 'timeline' && <div className="mb-8 bg-white rounded-lg shadow p-6" data-id="element-569">
          <div className="flex items-center justify-between mb-6" data-id="element-570">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-571">
              Policy Change Timeline
            </h2>
            <select className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-572">
              <option data-id="element-573">Last 90 Days</option>
              <option data-id="element-574">Last 6 Months</option>
              <option data-id="element-575">Last 12 Months</option>
            </select>
          </div>
          <PolicyChangeTimeline events={policyChangeEvents} data-id="element-576" />
        </div>}

      {viewMode === 'trends' && <div className="grid grid-cols-1 gap-6 mb-8 lg:grid-cols-2" data-id="element-577">
          <div className="p-5 bg-white rounded-lg shadow" data-id="element-578">
            <div className="flex items-center justify-between mb-4" data-id="element-579">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-580">
                Coverage Trend by Payer Type
              </h3>
              <select className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-581">
                <option data-id="element-582">Last 12 Months</option>
                <option data-id="element-583">Last 24 Months</option>
              </select>
            </div>
            <div className="h-64" data-id="element-584">
              <ResponsiveContainer width="100%" height="100%" data-id="element-585">
                <LineChart data={[{
              month: 'Jul',
              commercial: 32,
              medicare: 45,
              medicaid: 28
            }, {
              month: 'Aug',
              commercial: 35,
              medicare: 43,
              medicaid: 30
            }, {
              month: 'Sep',
              commercial: 38,
              medicare: 40,
              medicaid: 32
            }, {
              month: 'Oct',
              commercial: 40,
              medicare: 42,
              medicaid: 35
            }, {
              month: 'Nov',
              commercial: 42,
              medicare: 45,
              medicaid: 38
            }, {
              month: 'Dec',
              commercial: 38,
              medicare: 48,
              medicaid: 40
            }, {
              month: 'Jan',
              commercial: 35,
              medicare: 50,
              medicaid: 42
            }, {
              month: 'Feb',
              commercial: 32,
              medicare: 53,
              medicaid: 45
            }, {
              month: 'Mar',
              commercial: 30,
              medicare: 55,
              medicaid: 48
            }, {
              month: 'Apr',
              commercial: 32,
              medicare: 58,
              medicaid: 50
            }, {
              month: 'May',
              commercial: 35,
              medicare: 60,
              medicaid: 52
            }, {
              month: 'Jun',
              commercial: 38,
              medicare: 62,
              medicaid: 55
            }]} data-id="element-586">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" data-id="element-587" />
                  <XAxis dataKey="month" data-id="element-588" />
                  <YAxis data-id="element-589" />
                  <Tooltip data-id="element-590" />
                  <Line type="monotone" dataKey="commercial" stroke="#3b82f6" strokeWidth={2} dot={false} data-id="element-591" />
                  <Line type="monotone" dataKey="medicare" stroke="#8b5cf6" strokeWidth={2} dot={false} data-id="element-592" />
                  <Line type="monotone" dataKey="medicaid" stroke="#ec4899" strokeWidth={2} dot={false} data-id="element-593" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center mt-4 space-x-6" data-id="element-594">
              <div className="flex items-center" data-id="element-595">
                <div className="w-3 h-3 bg-blue-500 rounded-full" data-id="element-596"></div>
                <span className="ml-2 text-xs text-gray-600" data-id="element-597">Commercial</span>
              </div>
              <div className="flex items-center" data-id="element-598">
                <div className="w-3 h-3 bg-purple-500 rounded-full" data-id="element-599"></div>
                <span className="ml-2 text-xs text-gray-600" data-id="element-600">Medicare</span>
              </div>
              <div className="flex items-center" data-id="element-601">
                <div className="w-3 h-3 bg-pink-500 rounded-full" data-id="element-602"></div>
                <span className="ml-2 text-xs text-gray-600" data-id="element-603">Medicaid</span>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white rounded-lg shadow" data-id="element-604">
            <div className="flex items-center justify-between mb-4" data-id="element-605">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-606">
                Upcoming Policy Reviews
              </h3>
              <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-607">
                View All
              </button>
            </div>
            <div className="overflow-hidden" data-id="element-608">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-609">
                <thead className="bg-gray-50" data-id="element-610">
                  <tr data-id="element-611">
                    <th scope="col" className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase" data-id="element-612">
                      Payer
                    </th>
                    <th scope="col" className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase" data-id="element-613">
                      Policy
                    </th>
                    <th scope="col" className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase" data-id="element-614">
                      Review Date
                    </th>
                    <th scope="col" className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase" data-id="element-615">
                      Impact
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-616">
                  {[{
                payer: 'Cigna',
                policy: 'Peripheral Nerve Stimulation',
                date: 'Jul 15, 2025',
                impact: 'high'
              }, {
                payer: 'Anthem',
                policy: 'Neurostimulation Devices',
                date: 'Jul 22, 2025',
                impact: 'medium'
              }, {
                payer: 'Humana',
                policy: 'Pain Management Procedures',
                date: 'Aug 03, 2025',
                impact: 'medium'
              }, {
                payer: 'BCBS Michigan',
                policy: 'Peripheral Nerve Field Stimulation',
                date: 'Aug 15, 2025',
                impact: 'high'
              }, {
                payer: 'Kaiser Permanente',
                policy: 'Implantable Pain Control Devices',
                date: 'Aug 28, 2025',
                impact: 'low'
              }].map((review, index) => <tr key={index} className="hover:bg-gray-50" data-id="element-617">
                      <td className="px-4 py-3 text-sm text-blue-600 whitespace-nowrap" data-id="element-618">
                        {review.payer}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap" data-id="element-619">
                        {review.policy}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap" data-id="element-620">
                        {review.date}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" data-id="element-621">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${review.impact === 'high' ? 'bg-red-100 text-red-800' : review.impact === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`} data-id="element-622">
                          {review.impact.charAt(0).toUpperCase() + review.impact.slice(1)}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>}
    </div>;
};
export default Dashboard;